import type {IObjectCounter, IOperationResponse, IPagination} from "@localization/siteMap/siteMapInterfaces.ts";
import React, {useCallback, useEffect, useMemo, useState} from "react";
import {
    Autocomplete,
    Box,
    Grid,
    IconButton,
    LinearProgress,
    Paper,
    TextField,
    Tooltip,
    Typography
} from "@mui/material";
import {ArrowLeft, ArrowRight, GppBad, Info} from "@mui/icons-material";
import {useAlert} from "@contexts/hooks/useAlert.ts";

// Define a standard pagination interface if not already globally defined
export interface DefaultPesquisaProps<T> {
    onSelect?: (item?: T) => void;
    clearOnSelect?: boolean;
    overrides?: {
        label?: string;
        required?: boolean;
        disabled?: boolean;
    };
    defaultValue?: T;
    focused?: boolean;
}

// Todo: Refatorar pra aplicar o novo Get no lugar do searchFn
interface PesquisaAutocompleteProps<T> {
    // --- Required Props ---
    /** Function to fetch search results from the API */
    searchFn: (filter: string, pagination: IPagination) => Promise<IOperationResponse<T | T[], IObjectCounter[]>>;
    /** Function to convert an item of type T to a display string for the Autocomplete */
    itemToString: (item: T) => string;
    /** Callback function invoked when an item is selected */
    onItemSelected: (item?: T) => void;
    /** Label for the TextField input */
    label: string;
    // --- Optional Props ---
    /** Placeholder text for the TextField input */
    placeholder?: string;
    /** If true, clears the input field after an item is selected */
    clearOnSelect?: boolean;
    /** Function to render the content for the information popup when the Info icon is clicked */
    renderInfoContent?: (item?: T, popUpIndex?: number) => React.ReactNode;
    renderInfoContentWithoutItem?: boolean;
    /** Title for the information popup */
    infoPopupTitle?: string;
    /** Initial value for the search input */
    initialValue?: string;
    /** Debounce time in milliseconds for the search API call */
    debounceTime?: number;
    /** Text displayed while loading results */
    loadingText?: string;
    pageSize?: number;
    /** Pass through props to the Autocomplete component */
    autocompleteProps?: Omit<React.ComponentProps<typeof Autocomplete>, 'renderInput' | 'options' | 'onChange' | 'onInputChange' | 'value' | 'loading'>;
    /** Pass through props to the TextField component */
    textFieldProps?: Omit<React.ComponentProps<typeof TextField>, 'label' | 'placeholder' | 'variant' | 'type'>;
    defaultValue?: T;
    focused?: boolean;
}

/**
 * @deprecated esse cara tem problema de performance usar o {@link DefaultPesquisaAuto}
 * @param searchFn
 * @param itemToString
 * @param onItemSelected
 * @param label
 * @param placeholder
 * @param clearOnSelect
 * @param renderInfoContent
 * @param infoPopupTitle
 * @param initialValue
 * @param debounceTime
 * @param loadingText
 * @param pageSize
 * @param autocompleteProps
 * @param textFieldProps
 * @param renderInfoContentWithoutItem
 * @param defaultValue
 * @param focused
 * @constructor
 */
export const PesquisaAutocomplete = <T extends {}>({ // Constraint T to be a non-nullable object type
                                                       searchFn,
                                                       itemToString,
                                                       onItemSelected,
                                                       label,
                                                       placeholder,
                                                       clearOnSelect = false,
                                                       renderInfoContent,
                                                       infoPopupTitle = "Informações", // Default title
                                                       initialValue = "",
                                                       debounceTime = 750,
                                                       loadingText = "Carregando...",
                                                       pageSize = 100, // Default page size
                                                       autocompleteProps,
                                                       textFieldProps,
                                                       renderInfoContentWithoutItem = false,
                                                       defaultValue,
                                                       focused
                                                   }: PesquisaAutocompleteProps<T>) => {
    const {addPopUp, closePopUp} = useAlert();
    const [searchFilter, setSearchFilter] = useState<string>(defaultValue ? itemToString(defaultValue) : initialValue);
    const [items, setItems] = useState<T[]>(defaultValue && [defaultValue] || []);
    const [selected, setSelected] = useState<T | undefined>(defaultValue ?? undefined);
    const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout | null>(null);
    const [lastSearch, setLastSearch] = useState<{
        value: string | null,
        page: number
    }>({value: (defaultValue ? itemToString(defaultValue) : null), page: 1});
    const [unauthorized, setUnauthorized] = useState(false);
    const [popUpIndex, setPopUpIndex] = useState<string | null>(null);
    const [pagination, setPagination] = useState<IPagination>({page: 1, pageSize: 10});


    // Find the selected item based on the current search filter and items list
    useEffect(() => {
        const foundItem = items.find(item => itemToString(item) === searchFilter) ?? undefined;
        // Only update selected state if it actually changed to avoid unnecessary re-renders/effects
        if (foundItem !== selected) {
            setSelected(foundItem);
        }
    }, [searchFilter, items, itemToString, selected]); // Added selected dependency


    // Debounced search effect
    useEffect(() => {
        if (timeoutId) {
            clearTimeout(timeoutId);
        }

        const trimmedFilter = searchFilter.trim();

        // Set timeout to fetch data
        const newTimeoutId = setTimeout(() => {
            // Avoid searching if the selected item's string representation perfectly matches the filter
            // This prevents re-fetching when an item is selected from the dropdown
            // But we should still search if the filter is different from the selected item's string representation
            if (selected && itemToString(selected) === searchFilter) {
                setPagination(prev => ({...prev, loading: false})); // Ensure loading is off if we skip fetch
                return;
            }
            // Skip search if the filter is empty and there's a selected item
            // This prevents losing the reference when clicking on the search field again
            // But allow search to proceed if the filter is not empty, even if there's a selected item
            if (trimmedFilter === '' && selected) {
                setPagination(prev => ({...prev, loading: false}));
                return;
            }
            // Skip search if the filter is the same as the last search
            // This prevents duplicate searches
            // But we should still allow the search to proceed if the user is actively typing
            if (trimmedFilter === lastSearch.value && lastSearch.page === pagination.page) {
                setPagination(prev => ({...prev, loading: false})); // Ensure loading is off if we skip fetch
                return;
            }

            setPagination(prev => ({...prev, loading: true, page: 1}));
            setLastSearch(prev => ({...prev, value: trimmedFilter, page: prev.page})); // Update last search filter
            searchFn(trimmedFilter, pagination)
                .then((result) => {
                    // Ensure result and items are valid arrays
                    let newItems = Array.isArray(result?.items) ? result.items : [];

                    // If there's a selected item and it's not in the new items, add it to maintain the reference
                    if (selected && !newItems.some(item => itemToString(item) === itemToString(selected))) {
                        newItems = [selected, ...newItems];
                    }

                    setItems([...newItems]);
                    setPagination(prev => ({...prev, ...result.pagination}));

                    if (result.status == 'Unauthorized') {
                        setUnauthorized(true);
                    }
                })
                .catch(error => {
                    console.error("Error fetching data:", error);
                    setItems([]); // Clear items on error
                    // Optionally, show an error message to the user via setPopUp or another mechanism
                })
                .finally(() => {
                    setPagination(prev => ({...prev, loading: false, ignoreCache: false}));
                });
        }, debounceTime);

        setTimeoutId(newTimeoutId);

        // Cleanup function to clear timeout on unmount or when dependencies change
        return () => {
            if (newTimeoutId) {
                clearTimeout(newTimeoutId);
            }
        };
        // Only include dependencies that should trigger a new search debounce cycle
    }, [searchFilter, searchFn, debounceTime, pageSize, selected, itemToString, pagination.page]); // Added selected and itemToString

    // Effect to notify parent component about selection changes
    useEffect(() => {
        onItemSelected(selected);
        if (selected && clearOnSelect) {
            setSearchFilter(''); // Clear input only if an item is selected and clearOnSelect is true
            if (popUpIndex !== null) {
                closePopUp({uid: popUpIndex}); // Close the popup if it was opened
                setPopUpIndex(null); // Reset popUpIndex after closing
            }
        }
    }, [selected, clearOnSelect]); // Keep onItemSelected out of deps to avoid potential loops if parent re-creates it

    // Handlers
    const handleInputChange = useCallback((_event: React.SyntheticEvent, newValue: string) => {
        setSearchFilter(newValue);
        // If the user is typing and the new value doesn't match the selected item's string representation,
        // clear the selected state to allow the search to proceed
        if (selected && newValue !== itemToString(selected)) {
            setSelected(undefined);
        }
    }, [selected, itemToString]);


    const handleChange = useCallback((_event: React.SyntheticEvent, newValue: string | T | undefined) => {
        // If newValue is a string, it's likely coming from freeSolo input or clearing
        // If newValue is an object (T), it's from selecting an option
        if (typeof newValue === 'string') {
            setSearchFilter(newValue);
            // Explicitly set selected to null if the input doesn't match any known item string
            if (!items.some(item => itemToString(item) === newValue)) {
                setSelected(undefined);
            }
        } else if (newValue) { // newValue is of type T
            setSearchFilter(itemToString(newValue)); // Update input field to match selection
            setSelected(newValue); // Update selected state
        } else { // newValue is null (cleared)
            setSearchFilter('');
            setSelected(undefined);
        }
    }, [itemToString, items]);
    // Todo: essa porra ficou boa pra caralho, depois fazer isso uma função reutilizavel
    const escapeRegExp = useCallback((s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), []);

    const highlightParts = useCallback((text: string, query: string, minLenght: number = 2): React.ReactNode[] => {
        const terms = query
            .split(/\s+/)
            .map(t => t.trim())
            .filter(x => x.length > minLenght)
            .filter(Boolean);
        if (terms.length === 0) {
            return [
                <Typography key="full" component="span" sx={{whiteSpace: 'pre-wrap', display: 'inline'}}>
                    {text}
                </Typography>
            ];
        }
        const pattern = new RegExp(`(${terms.map(escapeRegExp).join('|')})`, 'ig');
        const parts = text.split(pattern);
        return parts.map((part, idx) => {
            if (part === null || part === undefined) return null;
            // Mantém exatamente o conteúdo (inclui espaços se estiverem nos fragments adjacentes)
            const isMatch = terms.some(t => part.toLowerCase() === t.toLowerCase());
            if (isMatch) {
                return (
                    <Typography
                        key={`m-${idx}`}
                        color={'info'}
                        component="span"
                        sx={{fontWeight: 800, display: 'inline', whiteSpace: 'pre-wrap'}}
                    >
                        {part}
                    </Typography>
                );
            }
            return (
                <Typography
                    key={`t-${idx}`}
                    component="span"
                    sx={{display: 'inline', whiteSpace: 'pre-wrap'}}
                >
                    {part}
                </Typography>
            );
        }).filter(Boolean) as React.ReactNode[];
    }, [escapeRegExp]);

    useEffect(() => {
        const foundItem = items.find(item => itemToString(item) === searchFilter) ?? undefined;
        // Only update selected state if it actually changed to avoid unnecessary re-renders/effects
        if (foundItem !== selected) {
            setSelected(foundItem);
        }
    }, [searchFilter, items, itemToString, selected]); // Added selected dependency


    const openInfoPopup = useCallback(() => {
        if ((selected || renderInfoContentWithoutItem) && renderInfoContent) {
            if (renderInfoContent) {
                setPopUpIndex(addPopUp({
                    title: infoPopupTitle,
                    content: renderInfoContent(selected ?? undefined),
                    size: 'lg',
                }));
            }
        }
    }, [selected, renderInfoContent, infoPopupTitle]);


    const options = useMemo<string[]>(() => {
        return items.map(itemToString)
    }, [items]);


    // TODO: entender por que essa desgraça aponta erro
    return (
        <Paper elevation={3}>
            {/* @ts-ignore o importante é que funciona...*/}
            <Autocomplete
                {...autocompleteProps} // Spread additional Autocomplete props
                size={'medium'}
                value={searchFilter}
                options={[...options]}
                loading={pagination.loading}
                loadingText={loadingText}
                disabled={unauthorized || (textFieldProps?.disabled ?? false)}
                onInputChange={handleInputChange}
                onFocus={() => {
                    // Only reset search filter if no item is selected
                    if (!selected || selected == defaultValue) {
                        setSearchFilter(lastSearch.value ?? '');
                    }
                }}
                onChange={(e, v) => handleChange(e, v as (string | T | undefined))}
                freeSolo
                filterOptions={(x) => x} // Disable built-in filtering as results come from API
                renderInput={(params) => (
                    <Grid size={12} container>
                        <Grid size={12}>
                            <Box sx={{display: 'flex', alignItems: 'center'}}>
                                {unauthorized &&
                                    <Tooltip title={'Sem permissão para utilizar'}><IconButton color={'error'}>
                                        <GppBad/>
                                    </IconButton>
                                    </Tooltip>}
                                <Tooltip title={'Pagina anterior'}>
                                    <Grid size={'auto'}>
                                        <IconButton color={'info'} onClick={() => {
                                            setPagination(prev => ({...prev, page: prev.page - 1, loading: true}))
                                        }}
                                                    disabled={pagination.page <= 1 || pagination.loading}>
                                            <ArrowLeft/>
                                        </IconButton>
                                    </Grid>
                                </Tooltip>
                                <TextField
                                    {...params}
                                    {...textFieldProps}
                                    sx={{minWidth: '150px'}}
                                    label={unauthorized ? 'Você não possui permissão para utilizar essa consulta!' : label}
                                    error={unauthorized || (textFieldProps?.required && (clearOnSelect != true && selected == undefined))}
                                    placeholder={placeholder}
                                    variant="filled"
                                    type="text"
                                    focused={focused || textFieldProps?.required && (clearOnSelect != true && selected == undefined)}
                                />
                                {renderInfoContent && (
                                    <IconButton
                                        sx={{height: '100%', maxHeight: '100%', color: 'primary.main'}}
                                        onClick={openInfoPopup}
                                        size="large"
                                        disabled={!renderInfoContentWithoutItem && !selected}
                                        aria-label={`Informações sobre ${label}`}
                                    >
                                        <Info/>
                                    </IconButton>
                                )}
                                <Tooltip title={'Pagina anterior'}>
                                    <Grid size={'auto'}>
                                        <IconButton color={'info'} onClick={() => {
                                            setPagination(prev => ({...prev, page: prev.page + 1, loading: true}))
                                        }} disabled={!pagination.hasNext || pagination.loading}>
                                            <ArrowRight/>
                                        </IconButton>
                                    </Grid>
                                </Tooltip>
                            </Box>
                            <Grid size={12}>
                                <LinearProgress value={pagination.loading ? 100 : 0}
                                                variant={pagination.loading ? 'indeterminate' : 'determinate'}/>
                            </Grid>
                        </Grid>
                    </Grid>
                )}
                renderOption={(props, option: unknown) => {
                    const optionStr = option as string;
                    const children = highlightParts(optionStr, searchFilter);
                    return (
                        <li {...props} key={optionStr}>
                            <Typography>{children.map((child) => (child))}</Typography>
                        </li>
                    );
                }}
                // Consider adding a 'noOptionsText' prop for better UX when search yields no results
                noOptionsText="Nenhum resultado encontrado"
            />
        </Paper>
    )
};
